import { Component, Injector, Input, OnInit } from '@angular/core';
import { WorkbenchConfiguratorItem } from '../../configurator/applications/workbench-configurator-item';
import { WorkbenchService } from '../../../../services/configurators-2d/applications/workbench.service';
import { ResumeDto } from 'src/app/models/configurators-2d/applications/workbench/resume-dto';
import { TranslateService } from '@ngx-translate/core';
import { IWorkbenchPreview } from '../preview/i-workbench-preview';
import { Configurators2DComponent } from '../../configurators-2d.component';
import { Ean } from 'src/app/models/configurators-2d/resume/ean';
import { TunnelService } from 'src/app/services/tunnel.service';

@Component({
  selector: 'ng-workbench-summary',
  templateUrl: './summary.component.html',
})
export class WorkbenchSummaryComponent extends WorkbenchConfiguratorItem implements OnInit {
  currentLang: string;
  isLoading: boolean;

  preview: IWorkbenchPreview;

  @Input() isOnlySplashback: boolean = false;

  constructor(
    workbenchService: WorkbenchService,
    private readonly translate: TranslateService,
    configurators2DComponent: Configurators2DComponent,
    private readonly injector: Injector
  ) {
    super(workbenchService);
    this.preview = configurators2DComponent.preview as IWorkbenchPreview;
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.currentLang = this.translate.getBrowserLang();
    this.preview.updateLegend(false);

    // Si des plans existent avec une couleur on affecte la couleur
    if (this.configuration.workbenchConfigurationPlans.length > 0) {
      this.configuration.workbenchConfigurationPlans.forEach(plan => {
        if (plan.color != null) {
          this.preview.loadImage(plan.color.picture.path, plan.displayOrder);
        }
      });
    }

    // Si des bords existent on récupère la valeur du sidecut
    if (
      this.configuration.workbenchConfigurationPlans.every(
        p => p?.materialThickness?.canHaveSideCuts /*&& p?.color?.range?.name === SILESTONE_NAME*/
      )
    ) {
      this.service.getSideCuts(this.configuration.workbenchConfigurationPlans[0].material.id).then(sideCuts => {
        for (let plan of this.configuration.workbenchConfigurationPlans) {
          for (let sideCut of (plan.configurationSideCuts ?? [])) {
            // La découpe qui n'est pas liée au jambage
            sideCut.sideCut = sideCuts.find(s => !s.isOnlyOnJamb);
            sideCut.sideCutId = sideCut.sideCut.id;
          }
        }
      });
    }

    this.preview.actionByConfiguratorStep();
    this.preview.previewSelectPlanById(null);

    this.configuration.isComplete = true;
    this.service.getResume<ResumeDto>().then(resume => {
      if (resume) {
        const tunnelService = this.injector.get(TunnelService);
        if (tunnelService.workbenchTunnelData) {
          tunnelService.workbenchTunnelData.resume = resume;
        }
        this.isLoading = false;
      }
    });
  }

  getCategories(): string[] {
    let propertyName = this.getPropertyNameLocalize('categoryName');

    let categories = [...new Map(this.configuration.resume.products.map(cp => [cp[propertyName], cp])).values()];

    if (this.isOnlySplashback) {
      categories = categories.filter(p => {
        const name = p.categoryNameFr?.trim().toLowerCase();
        return !name.startsWith('plan');
      });
    }

    return categories.map(cp => cp[propertyName]);
  }

  getProducts(categoryName: string): Ean[] {
    return this.configuration.resume.products.filter(
      e => e[this.getPropertyNameLocalize('categoryName')] === categoryName
    );
  }

  getPropertyNameLocalize(propertyName: string) {
    return propertyName + this.currentLang[0].toUpperCase() + this.currentLang.substring(1).toLowerCase();
  }

  isNextButtonActive(): boolean {
    return true;
  }

  checkPageChangeCapability(): boolean {
    const hasJambs = this.configuration.workbenchConfigurationPlans.some(p => p.configurationJambs?.length > 0);
    const allJambsAreComplete = this.configuration.workbenchConfigurationPlans.every(p =>
      p.configurationJambs?.every(j => j.height != null && j.height > 0)
    );

    return !hasJambs || (hasJambs && allJambsAreComplete);
  }

  getFilteredProducts(): Ean[] {
    if (this.isOnlySplashback) {
      return this.configuration.resume.products.filter(p => {
        const name = p.categoryNameFr?.trim().toLowerCase();
        return !name.startsWith('plan');
      });
    }
    return this.configuration.resume.products;
  }

  getTotalHT(): number {
    const tvaRate = this.configuration.resume.percentTVA ?? 20;
    return this.getFilteredProducts().reduce((sum, p) => {
      const pvHT = p.pv / (1 + tvaRate / 100);
      return sum + pvHT * p.quantity;
    }, 0);
  }

  getTotalTVA(): number {
    return this.getTotalTTC() - this.getTotalHT();
  }

  getTotalTTC(): number {
    return this.getFilteredProducts().reduce((sum, p) => sum + p.pv * p.quantity, 0);
  }
}
